<template>
  <div class="catalog-form">
    <h1 id="init">
      <i class="fal fa-arrow-circle-left" @click="onCancel" title="Regresar"></i>
      <span>{{ title }}</span>

      <div class="top-tool-bar flat actions">
        <div class="action d-none d-lg-block" title="Refrescar registro para obtener las últimas actualizaciones" v-if="canReadItem" @click="loadItem" variant="outline-info"><i class="fal fa-sync"></i> Refrescar</div>
        <div class="action d-none d-lg-block" title="Modificar" v-if="canModifyItem" @click="modifyItem" variant="outline-secondary"><i class="fal fa-file-edit"></i> Modificar</div>

        <div class="action d-lg-none d-sm-block" title="Refrescar registro para obtener las últimas actualizaciones" v-if="canReadItem" @click="loadItem" variant="outline-info"><i class="fal fa-sync"></i></div>
        <div class="action d-lg-none d-sm-block" title="Modificar" v-if="canModifyItem" @click="modifyItem" variant="outline-secondary"><i class="fal fa-file-edit"></i></div>
      </div>
    </h1>

    <div class="menu-area hide-md">
      <div class="list-group">
        <a href="#general" class="list-group-item list-group-item-action" aria-current="true">
          Información general
        </a>
        <a href="#grupos" class="list-group-item list-group-item-action" aria-current="true">
          Grupos
        </a>
        <a v-if="id" href="#history" class="list-group-item list-group-item-action" aria-current="true">
          Actualizaciones
        </a>
      </div>
    </div>

    <div class="form-area">
      <b-form @submit.stop.prevent="onSubmit" v-show="!loading">
        <!-- INFORMACION GENERAL -->
        <b-card id="general" header="Información general" class="form-section">
          <div class="row">
            <div class="col-sm-12 col-md-6">
              <b-form-group
              id="input-group-name"
              label="Nombre *"
              label-for="input-name">
                <b-form-input
                  id="input-project-name"
                  name="input-project-name"
                  type="text"
                  class="shadow-none"
                  placeholder="Nombre del proyecto"
                  v-model.trim="form.name"
                  v-validate="{ required: true }"
                  :state="validateState('input-project-name')"
                  aria-describedby="input-project-name-feedback"
                  :disabled="readOnly">
                </b-form-input>
                <b-form-invalid-feedback id="input-project-name-feedback">Nombre requerido</b-form-invalid-feedback>
              </b-form-group>
            </div>
            <div class="col-sm-12 col-md-6">
              <b-form-group
                id="select-group-domain"
                label="Dominio *"
                label-for="select-domain">
                <b-form-select
                  id="select-domain"
                  class="shadow-none"
                  name="select-domain"
                  v-model.trim="form.domain.id"
                  :options="domains"
                  :disabled="!canReadDomain || readOnly"
                  @change="initNewItem">
                </b-form-select>
                <small class="text-muted"> ID Dominio: {{form.domain.id}} </small>
              </b-form-group>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12 col-md-6">
              <b-form-checkbox
                id="checkbox-enable"
                v-model="form.enable"
                name="checkbox-enable"
                value="true"
                unchecked-value="false"
                :disabled="readOnly"
              >Habilitado
              </b-form-checkbox>
            </div>
          </div>
        </b-card>

        <!-- GRUPOS PERTENECIENTES -->
        <b-card id="grupos" header="Grupos pertenecientes" class="form-section users-groups">
          <div v-if="groups.length">
            <AdminGroupSelectionTreeItem v-for="it in groups" :key="it.id" :group="it" :root="true" :watchActive="watchGroupsTreeActive" :readOnly="readOnly" />
          </div>
          <div v-else>Sin grupos relacionados</div>
        </b-card>

        <!-- ACTUALIZACIONES -->
        <b-card id="history" header="Actualizaciones" class="form-section" v-if="id">
          <table class="table table-responsive-md">
            <thead>
              <tr>
                <th scope="col">Evento</th>
                <th scope="col">Usuario</th>
                <th scope="col">Fecha</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Creación</td>
                <td>{{ form.creationUser && form.creationUser.name || '---' }}</td>
                <td>{{ creationDate }}</td>
              </tr>
              <tr>
                <td>Actualización</td>
                <td>{{ form.updateUser && form.updateUser.name || '---' }}</td>
                <td>{{ updateDate }}</td>
              </tr>
            </tbody>
          </table>
        </b-card>

        <!-- ACCIONES -->
        <div class="text-right" v-if="!readOnly">
          <b-button class="mr-2" size="lg" @click="onCancel">Cancelar</b-button>
          <b-button type="submit" variant="primary" size="lg" v-if="!readOnly">Guardar Proyecto</b-button>
        </div>
      </b-form>

      <LoadingMask :active="loading"/>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator'
import { BasicEntity, Domain, DomainCatalogFilters, Group } from '@/types/Catalogs'
import { Project, ProjectCatalogFilters } from '@/types/catalogs/Projects'
import LoadingMask from '@/components/generic/LoadingMask.vue'
import { MessageError } from '@/components/generic/MessageHandling.vue'
import AdminGroupSelectionTreeItem from '../admin/AdminGroupSelectionTreeItem.vue'
import catalogsService from '@/services/CatalogsService'
import { PermissionNames } from '@/types/Auth'
import userInfoStore from '@/store/userInfo'
import moment from 'moment'
import DateFormat from '@/services/DateFormat'
import { PageRequest } from '@/types/Support'
import { GroupCatalogFilters, GroupStatus } from '@/types/catalogs/Groups'

@Component({
  components: {
    LoadingMask,
    MessageError,
    AdminGroupSelectionTreeItem
  }
})
export default class ProjectForm extends Vue {
  @Prop({ required: true, default: 0 })
  id: number

  @Prop({ required: true, default: '' })
  title: string

  @Prop({ required: false, default: false })
  readOnly: boolean

  groups: Group[] = []

  loading: boolean = false
  loadingGroups: boolean = false
  domains: any[] = []
  loadingDomains: boolean = false

  PAGE_SIZE = 500
  formBackup = ''
  form = Project.getInstanceForEdit()
  filters = new ProjectCatalogFilters()
  creationDate = ''
  updateDate = ''
  watchGroupsTreeActive = false

  /**
   * Loads form values in Vue mounted lifecycle
   */
  async mounted () {
    if (this.id) {
      await this.loadItem()
    } else {
      this.form.domain.id = userInfoStore.userInfo.domain.id
    }
    this.initDomain()
    this.callLoadGroups()
  }

  /**
   * Initializes the form domain and deselect subgroups
   */
  async initNewItem () {
    this.callLoadGroups()
    this.setSelectedGroups(this.groups, [])
    this.watchGroupsTreeActive = true
  }

  /**
   * Initialize the domain for the form
   */
  async initDomain () {
    if (userInfoStore.inRootDomain() && !this.readOnly) {
      await this.callLoadDomain()
    } else {
      let userDomain = userInfoStore.userInfo.domain
      this.domains.push({ text: userDomain.name, value: userDomain.id })
    }
    if (this.domains.findIndex(domain => domain.value === this.form.domain.id) === -1) {
      this.domains.push({ text: this.form.domain.name, value: this.form.domain.id })
    }
  }

  /**
   * Prepares a configuration request to get the domains
   * and call the method to get them
   */
  async callLoadDomain () {
    let pageRequest = new PageRequest<DomainCatalogFilters>(this.PAGE_SIZE)
    pageRequest.sort = 'name'
    await this.loadDomains(pageRequest)
  }

  /**
   * Configurations for a page request to get the group records
   *
   * @returns a PageRequest<GroupCatalogFilters>
   */
  prepareGroupRequest () {
    let pageRequest = new PageRequest<GroupCatalogFilters>()
    pageRequest.sort = 'name'
    pageRequest.filters.loadRelations = true
    if (userInfoStore.inRootDomain()) {
      pageRequest.filters.domain = this.form.domain.id
    }
    pageRequest.filters.status = GroupStatus.ACTIVE
    return pageRequest
  }

  /**
   * Prepares the page request to get the groups
   * and call the function to get them
   */
  async callLoadGroups () {
    this.groups = []
    let pageRequest = this.prepareGroupRequest()
    await this.loadGroups(pageRequest)
      .then(() => {
        this.initItemGroups()
      })
  }

  /**
   * Loads all the group records by api
   *
   * @param pageRequest configurations for the request
   */
  async loadGroups (pageRequest: PageRequest<GroupCatalogFilters>) {
    this.loadingGroups = true
    await catalogsService.getGroups(pageRequest)
      .then((response) => {
        this.groups = response.results
      })
      .catch((error) => {
        this.handleError(error, 'Error en carga de grupos')
      })
      .finally(() => { this.loadingGroups = false })
  }

  /**
   * Loads all the domain records to can select
   *
   * @param pageRequest whit the configurations for the request
   */
  async loadDomains (pageRequest: PageRequest<DomainCatalogFilters>) {
    this.loadingDomains = true
    await catalogsService.getDomains(pageRequest)
      .then((response) => {
        this.domains.push(
          ...response.results.map((domain: Domain) => {
            return { text: domain.name, value: domain.id }
          })
        )
        if (this.domains.length < response.total) {
          pageRequest.page++
          this.loadDomains(pageRequest)
        }
      })
      .catch((error) => {
        this.handleError(error, 'Error an carga de dominios')
      })
      .finally(() => { this.loadingDomains = false })
  }

  /**
   * Calls GET project request by id to API
   * and initialize the form values
   */
  async loadItem () {
    this.loading = true
    await catalogsService.getProjectById(this.id).then((data: Project) => {
      Object.assign(this.form, data)
      this.creationDate = data.creationDate ? new DateFormat(data.creationDate).toDateTimeString() : '- - / - - / - - - -'
      this.updateDate = data.updateDate ? new DateFormat(data.updateDate).toDateTimeString() : '- - / - - / - - - -'
      this.formBackup = JSON.stringify(this.form)
    }).finally(() => {
      this.loading = false
    })
  }

  /**
   * Initialize the status of the selected groups
   */
  initItemGroups () {
    const selectedIds = this.form.groups.reduce((arr: number[], it: BasicEntity) => arr.concat(it.id), [])
    this.setSelectedGroups(this.groups, selectedIds)
    setTimeout(() => {
      this.watchGroupsTreeActive = true
    }, 500)
  }

  /**
   * Gets the current selected groups
   * @param groups list to get the activated groups
   */
  getSelectedGroups (groups: Group[]): Group[] {
    let groupsObjects: Group[] = []
    groups.forEach(it => {
      if (it._selected) groupsObjects.push(it)
      if (it.subGroups && it.subGroups.length) groupsObjects = groupsObjects.concat(this.getSelectedGroups(it.subGroups))
    })
    return groupsObjects
  }

  /**
   * Active the status of the groups with base on list of active ids
   * @param groups to set status 'selected' or not
   * @param selectedIds with which the groups wll be activted
   */
  setSelectedGroups (groups: Group[], selectedIds: number[]) {
    groups.forEach(it => {
      selectedIds.indexOf(it.id) > -1 ? Vue.set(it, '_selected', true) : Vue.set(it, '_selected', false)
      if (it.subGroups && it.subGroups.length) this.setSelectedGroups(it.subGroups, selectedIds)
    })
  }

  /** Permissions */

  /**
   * Check if user has 'permission' permission
   * @param permission name to check
   */
  hasPermission (permission: string) {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === permission)
  }

  /**
   * Checks if user can read the item
   */
  get canReadItem () {
    return this.id && this.readOnly && this.hasPermission(PermissionNames.PROJECTS_READ)
  }

  /**
   * Checks if user can modify the item
   */
  get canModifyItem () {
    return this.id && this.readOnly && this.hasPermission(PermissionNames.PROJECTS_UPDATE)
  }

  /**
   * Checks if the user is root and can read the domain records
   */
  get canReadDomain () {
    return userInfoStore.inRootDomain() && this.hasPermission(PermissionNames.DOMAINS_READ)
  }

  /** CRUD */

  /**
   * Submits form values to save changes with a PUT/POST request
   */
  onSubmit () {
    this.$validator.validateAll().then((result) => {
      if (result) {
        // Get changes from selected groups
        this.form.groups = this.getSelectedGroups(this.groups)
        if (JSON.stringify(this.form) === this.formBackup) {
          // If there are no changes then return to main grid
          this.onCancel()
          return
        }
        this.form.updateDate = moment().toDate()
        this.saveProject(this.form)
      } else {
        this.$bvToast.toast('Revise los campos marcados con error', {
          title: 'Errores en el formulario',
          variant: 'danger',
          toaster: 'b-toaster-top-center',
          autoHideDelay: 4500
        })
      }
    })
  }

  /**
   * POST/PUT request to save the form values
   */
  saveProject (project: Project) {
    this.loading = true
    catalogsService.saveProject(project).then((response: Project) => {
      this.form.id = response.id
      this.$emit('saved', this.form)
    }).catch((error) => {
      this.handleError(error, 'Error al guardar el proyecto.')
    }).finally(() => {
      this.loading = false
    })
  }

  /**
   * GET Domain
   */
  async getDomain (id: number) {
    let domain = await catalogsService.getDomainById(id)
    return domain
  }

  /**
   * Emit event to modify item
   */
  modifyItem () {
    this.$emit('modify-item')
  }

  /**
   * Close form view
   */
  onCancel () {
    this.$emit('close')
  }

  /**
   * Checks if any form fields launchs an error
   */
  validateState (ref: any) {
    const thiz = this as any
    if (
      thiz.veeFields[ref] &&
      (thiz.veeFields[ref].dirty || thiz.veeFields[ref].validated)
    ) {
      return !thiz.veeErrors.has(ref)
    }
    return null
  }

  /**
   * Lauch error
   * @param error - error from users API action
   * @param title - to describe btoast
   */
  handleError (error: Error, title?: string) {
    let messageValues = {
      title: title,
      message: '',
      delay: 10000,
      error: error
    }
    let errorComponent = new MessageError({ propsData: messageValues })
    errorComponent.displayMessage()
  }
}
</script>

<style lang="scss" scoped>
$white:    #fff !default;
$gray-100: #f8f9fa !default;

.users-groups{
  .group-root{
    background-image: linear-gradient($gray-100, $white);
    border-radius: 7px;
  }
}
</style>
